import denmsReducer from '_store/denm/denmsReducers';
import { BlockedActionMode } from '_lib/auth/rbac';
import { Causes, Denm, DenmSourceFilter, SubCauses } from '_store/denm/denmsTypes';
import { showGeoNetworkingDistanceSelector } from '_store/denm/selectors';
import { hasDefinedPropertyDeep } from '_store/utils';
import { useLocalization } from '@fluent/react';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { faClone, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CardContent,
  FormControlLabel,
  Grid,
  Switch,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { blue, red } from '@material-ui/core/colors';
import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Accordion } from "../../../../items/Accordion";
import { RoleGuard } from '../../../../items/RoleGuard';
import { RsusMetaData } from '../../../Device/RsusMetaData';
import { EtsiMetaData } from './Etsi';
import { DenmOptionalDetail } from './Optional';
import { SincroMetaData } from './Sincro';
import styles from './styles';

export interface CardDetailsProps {
  denm: Denm;
  cause: Causes;
  subCause: SubCauses;
  onUpdate?: () => void;
  onDelete?: () => void;
  showButtons?: boolean;
  isActive?: boolean;
  onDisable?: () => void;
  onEnable?: () => void;
  isDisabled?: boolean;
  onClone?: () => void;
  writeAllowed?: boolean;
  blockedActionMode?: BlockedActionMode;
  blockedMessage?: string;
}

const CardDetails: FC<CardDetailsProps> = ({
  denm,
  cause,
  subCause,
  onUpdate,
  onDelete,
  showButtons,
  isActive = true,
  onDisable,
  onEnable,
  isDisabled = false,
  onClone,
  writeAllowed = true,
  blockedActionMode = 'disable',
  blockedMessage = '',
}) => {
  const classes = styles();
  const { l10n } = useLocalization();
  const dispatch = useDispatch();
  const showGeoNetworkingDistance = useSelector(showGeoNetworkingDistanceSelector);

  const handleGeoNetworkingDistanceToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(denmsReducer.actions.toggleGeoNetworkingDistance(event.target.checked));
  };

  return (
    <CardContent className={classes.detailStyle}>
      <Grid container spacing={1} style={{ marginTop: 10 }}>
        <Grid item xs={12}>
          <Typography component={'span'} variant={'body1'} paragraph>
            {l10n.getString('forms-denm-detail-header')}:
          </Typography>
        </Grid>
        <Grid container spacing={1} style={{ marginTop: 10 }}>
          <Grid item xs={12}>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              <strong>{`DENM #${denm.id}`}</strong>
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-denm-cause')} {`\n`}{' '}
              <strong>
                {cause ? cause.description : l10n.getString('detail-denm-missing-value-unknown')}
              </strong>
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-denm-subcause')}: {`\n`}{' '}
              <strong>
                {subCause
                  ? subCause.description
                  : l10n.getString('detail-denm-missing-value-unknown')}
              </strong>
            </Typography>
          </Grid>

          <Grid item xs={12} className={classes.gridVertical}>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-denm-init-date')} {`\n`}{' '}
              <strong>{new Date(denm.createdAt).toLocaleString()}</strong>
            </Typography>

            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-denm-final-date')} {`\n`}
              <strong>{new Date(denm.expiresAt).toLocaleString()}</strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-denm-reference-point')} {`\n`}
              <strong>
                ({denm.referencePoint.coordinates[1]}, {denm.referencePoint.coordinates[0]})
              </strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-denm-source')} {`\n`}
              <strong>
                {denm.source === DenmSourceFilter.CONNEX
                  ? l10n.getString('forms-event-source-vehicles')
                  : denm.source === DenmSourceFilter.DATEXII
                  ? l10n.getString('forms-event-source-datexii')
                  : denm.source}
              </strong>
            </Typography>
            {denm.source === DenmSourceFilter.DATEXII && (
              <Typography
                component={'span'}
                variant={'body2'}
                paragraph
                className={classes.textStyle}
              >
                {l10n.getString('forms-denm-datexii-id')} {`\n`}
                <strong>{denm.datexiiId}</strong>
              </Typography>
            )}

            {denm.optional?.geo_networking?.distance_a && (
              <Grid item xs={12} style={{ marginLeft: 5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showGeoNetworkingDistance}
                      onChange={handleGeoNetworkingDistanceToggle}
                      name="showGeoNetworkingDistance"
                      color="primary"
                      size="small"
                    />
                  }
                  label={l10n.getString('forms-denm-show-geonetworking-distance')}
                  classes={{ label: classes.smallLabel }}
                />
              </Grid>
            )}

            <DenmOptionalDetail
              denm={denm}
              showManagement={
                denm.optional?.management && hasDefinedPropertyDeep(denm.optional?.management)
              }
              showSituation={
                denm.optional?.situation && hasDefinedPropertyDeep(denm.optional?.situation)
              }
              showLocation={
                denm.optional?.location && hasDefinedPropertyDeep(denm.optional?.location)
              }
              showAlacarte={
                denm.optional?.alacarte && hasDefinedPropertyDeep(denm.optional?.alacarte)
              }
            />
            {denm.metadata?.etsi && <EtsiMetaData etsiData={denm.metadata?.etsi} />}
          </Grid>
          <Grid item xs={12}>
            {denm.metadata?.sincro && (
              <div style={{ marginTop: '20px' }}>
                <Accordion title={l10n.getString('view-details-metadata')}>
                  <SincroMetaData sincroData={denm.metadata.sincro} source={denm.source} />
                </Accordion>
              </div>
            )}
          </Grid>
          <Grid item xs={12}>
            {denm.metadata?.rsus && denm.metadata?.rsus.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <Accordion title={l10n.getString('view-selected-devices-denm')}>
                  <RsusMetaData rsus={denm.metadata.rsus} />
                </Accordion>
              </div>
            )}
          </Grid>
        </Grid>

        {denm.source === DenmSourceFilter.EMERAS && showButtons && (
          <Grid item xs={12} style={{ paddingLeft: 0, marginTop: 10 }}>
            {(isActive || isDisabled) && (
              <>
                {!isDisabled && (
                  <RoleGuard
                    allowed={writeAllowed}
                    blockedActionMode={blockedActionMode}
                    blockedMessage={blockedMessage}
                  >
                    <Tooltip title={l10n.getString('forms-event-update-btn')}>
                      <FontAwesomeIcon
                        icon={faPen}
                        style={{
                          backgroundColor: blue[400],
                        }}
                        className={classes.iconButton}
                        size="xs"
                        onClick={onUpdate}
                      />
                    </Tooltip>
                  </RoleGuard>
                )}
                <RoleGuard
                  allowed={writeAllowed}
                  blockedActionMode={blockedActionMode}
                  blockedMessage={blockedMessage}
                >
                  <Tooltip
                    title={l10n.getString(
                      isDisabled ? 'forms-event-enable-btn' : 'forms-event-disable-btn',
                    )}
                  >
                    <FontAwesomeIcon
                      icon={isDisabled ? faEye : faEyeSlash}
                      style={{
                        backgroundColor: blue[400],
                      }}
                      className={classes.iconButton}
                      size="xs"
                      onClick={isDisabled ? onEnable : onDisable}
                    />
                  </Tooltip>
                </RoleGuard>
              </>
            )}
            {!isActive && !isDisabled && (
              <RoleGuard
                allowed={writeAllowed}
                blockedActionMode={blockedActionMode}
                blockedMessage={blockedMessage}
              >
                <Tooltip title={l10n.getString('forms-event-clone-btn')}>
                  <FontAwesomeIcon
                    icon={faClone}
                    style={{
                      backgroundColor: blue[400],
                    }}
                    className={classes.iconButton}
                    size="xs"
                    onClick={onClone}
                  />
                </Tooltip>
              </RoleGuard>
            )}
            {(isActive || isDisabled) && (
              <RoleGuard
                allowed={writeAllowed}
                blockedActionMode={blockedActionMode}
                blockedMessage={blockedMessage}
              >
                <Tooltip title={l10n.getString('forms-event-delete-btn')}>
                  <FontAwesomeIcon
                    icon={faTrash}
                    style={{
                      backgroundColor: red[400],
                    }}
                    className={classes.iconButton}
                    size="xs"
                    onClick={onDelete}
                  />
                </Tooltip>
              </RoleGuard>
            )}
          </Grid>
        )}
      </Grid>
    </CardContent>
  );
};

export default React.memo(CardDetails);
